/**
 * Custom Scoring Models — Tenant-defined scoring configurations.
 *
 * Multiple models per tenant (Lead Score, Engagement, Churn Risk).
 * Each model: data sources + weights + custom rules + decay rate + tiers.
 * AI recommendations stored per model from vertical won-deal data.
 *
 * Design source: docs/masterplan/26-communications-intelligence-platform.md Section 22
 * API contract: POST /crm/scoring-models { name, dataSources, rules, decayRate }
 */

import { pgTable, uuid, varchar, jsonb, timestamp, boolean, real, index, uniqueIndex, check } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

/**
 * Data source entry in the dataSources JSONB array:
 * { type: "email_engagement", weight: 20, enabled: true }
 *
 * Supported types:
 * - email_engagement (opens, clicks)
 * - page_visits (which pages, depth, frequency)
 * - form_submissions (which form, fields)
 * - webinar_attendance (% watched)
 * - sms_whatsapp_replies
 * - deal_progression (stage changes)
 * - custom_webhook (external data)
 * - social_engagement
 * - course_progress
 */

/**
 * Custom rule entry in the rules JSONB array:
 * { condition: "title contains 'owner'", points: 20, label: "Business owner" }
 */

/**
 * Tier configuration in the tiers JSONB:
 * { hot: { min: 80, label: "Hot" }, warm: { min: 50, label: "Warm" }, cold: { min: 0, label: "Cold" } }
 */

export const scoringModels = pgTable("scoring_models", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: varchar("name", { length: 200 }).notNull(),
  description: varchar("description", { length: 500 }),
  dataSources: jsonb("data_sources").notNull().$type<Array<{
    type: string;
    weight: number;
    enabled: boolean;
  }>>(),
  rules: jsonb("rules").notNull().$type<Array<{
    condition: string;
    points: number;
    label: string;
  }>>(),
  decayRatePerWeek: real("decay_rate_per_week").notNull().default(5), // 5% per week default
  tiers: jsonb("tiers").notNull().$type<{
    hot: { min: number; label: string };
    warm: { min: number; label: string };
    cold: { min: number; label: string };
  }>(),
  isDefault: boolean("is_default").notNull().default(false),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  aiRecommendations: jsonb("ai_recommendations").$type<Array<{
    suggestion: string;
    reason: string;
    appliedAt?: string;
  }>>(),
  createdBy: uuid("created_by").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index("idx_scoring_models_tenant").on(table.tenantId),
  index("idx_scoring_models_tenant_status").on(table.tenantId, table.status),
  uniqueIndex("uq_scoring_model_tenant_name").on(table.tenantId, table.name),
  check("ck_scoring_model_status", sql`${table.status} IN ('active', 'archived')`),
]);
